import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useMutation, useQuery, gql } from "@apollo/client";
import { format } from "date-fns";

const EDIT_CALAIM_NOTE = gql`
  mutation EditCalaimNote($noteId: Int!, $note: AddCalaimNoteInput!) {
    editCalaimNote(noteId: $noteId, note: $note) {
      message
    }
  }
`;

// Query to fetch Place of Service options from database
const GET_POS_OPTIONS = gql`
  query GetCalaimPosOptions {
    calaimPosOptions {
      pos_code
      pos_name
    }
  }
`;

// Query to fetch Procedure Type options from database
const GET_PROCEDURE_TYPES = gql`
  query GetCalaimProcedureTypes {
    calaimProcedureTypes {
      value
      label
    }
  }
`;

// Hardcoded procedure types from billing_proceduretypes table
const PROCEDURE_TYPE_OPTIONS = [
  { value: "Initial Assessment-first 90 days", label: "Initial Assessment-first 90 days" },
  { value: "Care Plan-every quarter or more", label: "Care Plan-every quarter or more" },
  { value: "Client Contact", label: "Client Contact" },
  { value: "Client Outreach", label: "Client Outreach" },
  { value: "Care Coordination", label: "Care Coordination" },
];

export const EditCalAIM = ({
  isOpen,
  onClose,
  calaimData,
  user,
}: {
  isOpen: boolean;
  onClose: () => void;
  calaimData: any;
  user: {
    name: string;
    username: string;
  };
}) => {
  // Fetch Place of Service options
  const { data: posData, loading: posLoading } = useQuery(GET_POS_OPTIONS);
  
  // Fetch Procedure Type options
  const { data: procedureTypeData, loading: procedureTypeLoading } = useQuery(GET_PROCEDURE_TYPES);

  const [formData, setFormData] = useState({
    calaim_pos: "",
    proceduremod1: "",
    proceduremod2: "",
    proceduretype: "",
    procedurelength: "",
    note_entry_date: "",
    procedurenote: "",
    procedureentery: "",
    isMdtConference: false,
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const [editCalaimNote, { loading: updateLoading }] = useMutation(EDIT_CALAIM_NOTE);

  useEffect(() => {
    if (calaimData) {
      setFormData({
        calaim_pos: calaimData.pos_code || "",
        proceduremod1: calaimData.proceduremod1 || "",
        proceduremod2: calaimData.proceduremod2 || "",
        proceduretype: calaimData.proceduretype || "",
        procedurelength: calaimData.procedurelength || "",
        note_entry_date: calaimData.note_entry_date || format(new Date(), "yyyy-MM-dd"),
        procedurenote: calaimData.procedurenote || "",
        procedureentery: calaimData.procedureentery || user?.name || user?.username || "",
        isMdtConference: calaimData.isMdtConference || false,
      });
    }
  }, [calaimData, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === "isMdtConference") {
      const isMdtConference = value === "true";
      setFormData((prev) => ({ 
        ...prev, 
        [name]: isMdtConference,
        // Clear modifier selections when MDT Conference is checked
        ...(isMdtConference && {
          proceduremod1: "",
          proceduremod2: ""
        })
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (formData.calaim_pos === "" || formData.calaim_pos === "0") {
      newErrors.calaim_pos = "Place of Service must be selected";
    }

    // Skip modifier validation if MDT Conference is selected
    if (!formData.isMdtConference) {
      if (!formData.proceduremod1) {
        newErrors.proceduremod1 = "In Person/Telehealth must be selected";
      }

      if (!formData.proceduremod2) {
        newErrors.proceduremod2 = "Clinical/NonClinical must be selected";
      }
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await editCalaimNote({
        variables: {
          noteId: parseInt(calaimData.id, 10),
          note: {
            pos_code: formData.calaim_pos,
            proceduremod1: formData.isMdtConference ? "" : formData.proceduremod1,
            proceduremod2: formData.isMdtConference ? "" : formData.proceduremod2,
            proceduretype: formData.proceduretype,
            procedurelength: formData.procedurelength,
            note_entry_date: formData.note_entry_date,
            procedurenote: formData.procedurenote,
            procedureentery: formData.procedureentery,
            isMdtConference: formData.isMdtConference
          },
        },
      });

      onClose();
    } catch (error) {
      console.error("Error updating CalAIM note:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-none max-h-[90vh] overflow-y-auto" style={{ width: '90vw', minWidth: '1200px' }}>
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            Edit CalAIM Note
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="calaim_pos">Place of Service</Label>
              <Select 
                onValueChange={(value) => handleSelectChange("calaim_pos", value)}
                value={formData.calaim_pos}
                disabled={posLoading}
              >
                <SelectTrigger className={errors.calaim_pos ? "border-red-500" : ""}>
                  <SelectValue placeholder="Select place of service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">-- Select an option --</SelectItem>
                  {posLoading ? (
                    <SelectItem value="loading">Loading...</SelectItem>
                  ) : (
                    posData?.calaimPosOptions
                      ?.filter((option: { pos_code: string; pos_name: string }) => 
                        option.pos_name === "Home" || 
                        option.pos_name === "Office" ||
                        option.pos_name === "Telehealth in pt home" ||
                        option.pos_name === "Telehealth outside pt home"
                      )
                      ?.map((option: { pos_code: string; pos_name: string }) => (
                        <SelectItem key={option.pos_code} value={option.pos_code}>
                          {option.pos_name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              {errors.calaim_pos && <p className="text-sm text-red-500">{errors.calaim_pos}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="note_entry_date">Note Date</Label>
              <Input
                type="date"
                id="note_entry_date"
                name="note_entry_date"
                value={formData.note_entry_date}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isMdtConference"
                checked={formData.isMdtConference}
                onCheckedChange={(checked) => handleSelectChange("isMdtConference", checked ? "true" : "false")}
              />
              <Label htmlFor="isMdtConference" className="font-medium">
                Multidisciplinary Team Conference (G9007)
              </Label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>In Person/Telehealth</Label>
              <RadioGroup 
                value={formData.proceduremod1} 
                onValueChange={(value) => handleSelectChange("proceduremod1", value)}
                className={errors.proceduremod1 ? "border border-red-500 rounded-md p-2" : ""}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="inperson" />
                  <Label htmlFor="inperson">In Person</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="0" id="telehealth" />
                  <Label htmlFor="telehealth">Telehealth</Label>
                </div>
              </RadioGroup>
              {errors.proceduremod1 && <p className="text-sm text-red-500">{errors.proceduremod1}</p>}
            </div>

            <div className="space-y-2">
              <Label>Clinical/NonClinical</Label>
              <RadioGroup 
                value={formData.proceduremod2} 
                onValueChange={(value) => handleSelectChange("proceduremod2", value)}
                className={errors.proceduremod2 ? "border border-red-500 rounded-md p-2" : ""}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="clinical" />
                  <Label htmlFor="clinical">Clinical</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="0" id="nonclinical" />
                  <Label htmlFor="nonclinical">NonClinical</Label>
                </div>
              </RadioGroup>
              {errors.proceduremod2 && <p className="text-sm text-red-500">{errors.proceduremod2}</p>}
            </div>

          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Procedure Type</Label>
              <Select
                onValueChange={(value) => handleSelectChange("proceduretype", value)}
                value={formData.proceduretype}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select procedure type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select an option --</SelectItem>
                  {PROCEDURE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Procedure Length</Label>
              <Select
                onValueChange={(value) => handleSelectChange("procedurelength", value)}
                value={formData.procedurelength}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select procedure length" />
                </SelectTrigger>
                <SelectContent>
                  {PROCEDURE_LENGTH_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="procedurenote">Procedure Note</Label>
            <Textarea
              id="procedurenote"
              name="procedurenote"
              value={formData.procedurenote}
              onChange={handleInputChange}
              placeholder="Enter your note here"
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="procedureentery">Entered By</Label>
            <Input
              id="procedureentery"
              name="procedureentery"
              value={formData.procedureentery}
              className="bg-gray-100"
              readOnly
              required
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={updateLoading}>
              {updateLoading ? "Saving..." : "Update Note"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// These match the procedure lengths from the PHP file
const PROCEDURE_LENGTH_OPTIONS = [
  { value: "15", label: "15 mins" },
  { value: "30", label: "30 mins" },
  { value: "45", label: "45 mins" },
  { value: "60", label: "60 mins" },
  { value: "75", label: "1hour 15mins" },
  { value: "90", label: "1hour 30mins" },
  { value: "105", label: "1hour 45mins" },
  { value: "120", label: "2hours" },
  { value: "135", label: "2hours 15mins" },
  { value: "150", label: "2hours 30mins" },
  { value: "165", label: "2hours 45mins" },
  { value: "180", label: "3hours" },
  { value: "195", label: "3hours 15mins" },
  { value: "210", label: "3hours 30mins" },
  { value: "225", label: "3hours 45mins" },
  { value: "240", label: "4hours" }
];
